import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  XMarkIcon, 
  PlusIcon, 
  TrashIcon, 
  CheckIcon,
  CameraIcon,
  VideoCameraIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CogIcon,
  CloudIcon,
  PhoneIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline'
import { Shoot, ShotListItem, KitListItem } from '../types'

interface ShootDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  shoot: Shoot | null
  onSave: (shootId: string, updates: Partial<Shoot>) => Promise<void>
  loading?: boolean
}

const ShootDetailsModal: React.FC<ShootDetailsModalProps> = ({
  isOpen,
  onClose,
  shoot,
  onSave,
  loading = false
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'shot-list' | 'kit-list' | 'prep-notes'>('overview')
  const [formData, setFormData] = useState<Partial<Shoot>>({})
  const [shotList, setShotList] = useState<ShotListItem[]>([])
  const [kitList, setKitList] = useState<KitListItem[]>([])
  const [newShotItem, setNewShotItem] = useState<Partial<ShotListItem>>({})
  const [newKitItem, setNewKitItem] = useState<Partial<KitListItem>>({})
  const [showAddShot, setShowAddShot] = useState(false)
  const [showAddKit, setShowAddKit] = useState(false)

  // Initialize form data when shoot changes
  useEffect(() => {
    if (shoot) {
      setFormData({
        weather_notes: shoot.weather_notes || '',
        parking_info: shoot.parking_info || '',
        access_instructions: shoot.access_instructions || '',
        emergency_contact: shoot.emergency_contact || '',
        special_requirements: shoot.special_requirements || '',
        client_prep_notes: shoot.client_prep_notes || '',
        backup_plan: shoot.backup_plan || '',
        post_shoot_notes: shoot.post_shoot_notes || ''
      })
      setShotList(shoot.shot_list || [])
      setKitList(shoot.kit_list || [])
      // Initialize with default values
      setNewKitItem({
        item: '',
        category: 'camera',
        quantity: 1,
        notes: ''
      })
    }
  }, [shoot])

  const handleInputChange = (field: keyof Shoot, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const addShotItem = () => {
    if (newShotItem.title && newShotItem.description) {
      const shotItem: ShotListItem = {
        title: newShotItem.title,
        description: newShotItem.description,
        priority: newShotItem.priority || 'medium',
        estimated_time: newShotItem.estimated_time,
        notes: newShotItem.notes
      }
      setShotList(prev => [...prev, shotItem])
      setNewShotItem({})
      setShowAddShot(false)
    }
  }

  const removeShotItem = (index: number) => {
    setShotList(prev => prev.filter((_, i) => i !== index))
  }

  const toggleShotCompleted = (index: number) => {
    setShotList(prev => prev.map((item, i) => 
      i === index ? { ...item, completed: !item.completed } : item
    ))
  }

  const addKitItem = () => {
    console.log('addKitItem called', { newKitItem })
    if (newKitItem.item && newKitItem.category) {
      console.log('Validation passed, creating kit item')
      const kitItem: KitListItem = {
        item: newKitItem.item,
        category: newKitItem.category as 'camera' | 'lens' | 'lighting' | 'audio' | 'accessories' | 'backup' | 'other',
        quantity: newKitItem.quantity || 1,
        checked: false,
        notes: newKitItem.notes
      }
      console.log('Kit item to add:', kitItem)
      setKitList(prev => {
        console.log('Previous kit list:', prev)
        const newList = [...prev, kitItem]
        console.log('New kit list:', newList)
        return newList
      })
      // Reset to default values instead of empty object
      setNewKitItem({
        item: '',
        category: 'camera',
        quantity: 1,
        notes: ''
      })
      setShowAddKit(false)
    } else {
      console.log('Validation failed', { 
        hasItem: !!newKitItem.item, 
        hasCategory: !!newKitItem.category,
        newKitItem 
      })
    }
  }

  const removeKitItem = (index: number) => {
    setKitList(prev => prev.filter((_, i) => i !== index))
  }

  const toggleKitItemChecked = (index: number) => {
    setKitList(prev => prev.map((item, i) => 
      i === index ? { ...item, checked: !item.checked } : item
    ))
  }

  const handleSave = async () => {
    if (shoot) {
      const updates = {
        ...formData,
        shot_list: shotList,
        kit_list: kitList
      }
      await onSave(shoot.id, updates)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-500 bg-red-500/10'
      case 'high': return 'text-orange-500 bg-orange-500/10'
      case 'medium': return 'text-yellow-500 bg-yellow-500/10'
      case 'low': return 'text-green-500 bg-green-500/10'
      default: return 'text-gray-500 bg-gray-500/10'
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'camera': return <CameraIcon className="w-4 h-4" />
      case 'lens': return <CogIcon className="w-4 h-4" />
      case 'lighting': return <WrenchScrewdriverIcon className="w-4 h-4" />
      case 'audio': return <PhoneIcon className="w-4 h-4" />
      case 'accessories': return <DocumentTextIcon className="w-4 h-4" />
      case 'backup': return <CloudIcon className="w-4 h-4" />
      default: return <CogIcon className="w-4 h-4" />
    }
  }

  const kitCompletionPercentage = kitList.length > 0 
    ? Math.round((kitList.filter(item => item.checked).length / kitList.length) * 100)
    : 0

  if (!shoot) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden bg-gray-900 rounded-2xl border border-gray-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div className="flex items-center space-x-3">
                {shoot.type === 'photo' ? (
                  <CameraIcon className="w-6 h-6 text-blue-400" />
                ) : (
                  <VideoCameraIcon className="w-6 h-6 text-purple-400" />
                )}
                <div>
                  <h2 className="text-xl font-semibold text-white">{shoot.title}</h2>
                  <p className="text-sm text-gray-400">
                    {new Date(shoot.date).toLocaleDateString()} • {shoot.start_time} - {shoot.end_time}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-700">
              {[
                { id: 'overview', label: 'Overview', icon: DocumentTextIcon },
                { id: 'shot-list', label: 'Shot List', icon: CameraIcon },
                { id: 'kit-list', label: 'Kit List', icon: WrenchScrewdriverIcon },
                { id: 'prep-notes', label: 'Prep Notes', icon: ExclamationTriangleIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-400/5'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Client Information
                          </label>
                          <div className="bg-gray-800 rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-2">
                              <UserIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-white font-medium">{shoot.client?.name}</span>
                            </div>
                            <div className="text-sm text-gray-400 space-y-1">
                              <p>{shoot.client?.email}</p>
                              {shoot.client?.phone && <p>{shoot.client.phone}</p>}
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Location & Time
                          </label>
                          <div className="bg-gray-800 rounded-lg p-4 space-y-3">
                            {shoot.location && (
                              <div className="flex items-center space-x-2">
                                <MapPinIcon className="w-4 h-4 text-gray-400" />
                                <span className="text-white">{shoot.location}</span>
                              </div>
                            )}
                            <div className="flex items-center space-x-2">
                              <ClockIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-white">
                                {new Date(shoot.date).toLocaleDateString()} • {shoot.start_time} - {shoot.end_time}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Quick Stats
                          </label>
                          <div className="bg-gray-800 rounded-lg p-4 space-y-3">
                            <div className="flex justify-between">
                              <span className="text-gray-400">Shot List Items:</span>
                              <span className="text-white font-medium">{shotList.length}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Kit Items:</span>
                              <span className="text-white font-medium">{kitList.length}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Kit Packed:</span>
                              <span className="text-white font-medium">{kitCompletionPercentage}%</span>
                            </div>
                          </div>
                        </div>

                        {shoot.notes && (
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              General Notes
                            </label>
                            <div className="bg-gray-800 rounded-lg p-4">
                              <p className="text-white text-sm">{shoot.notes}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Shot List Tab */}
                {activeTab === 'shot-list' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-white">Shot List</h3>
                      <button
                        onClick={() => setShowAddShot(true)}
                        className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        <span>Add Shot</span>
                      </button>
                    </div>

                    {showAddShot && (
                      <div className="bg-gray-800 rounded-lg p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
                            <input
                              type="text"
                              value={newShotItem.title || ''}
                              onChange={(e) => setNewShotItem(prev => ({ ...prev, title: e.target.value }))}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Wide establishing shot"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                            <select
                              value={newShotItem.priority || 'medium'}
                              onChange={(e) => setNewShotItem(prev => ({ ...prev, priority: e.target.value as any }))}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="critical">Critical</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Description *</label>
                          <textarea
                            value={newShotItem.description || ''}
                            onChange={(e) => setNewShotItem(prev => ({ ...prev, description: e.target.value }))}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            placeholder="Describe the shot, composition, lighting, etc."
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Estimated Time (minutes)</label>
                            <input
                              type="number"
                              value={newShotItem.estimated_time || ''}
                              onChange={(e) => setNewShotItem(prev => ({ ...prev, estimated_time: parseInt(e.target.value) || undefined }))}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="15"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                            <input
                              type="text"
                              value={newShotItem.notes || ''}
                              onChange={(e) => setNewShotItem(prev => ({ ...prev, notes: e.target.value }))}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Additional notes"
                            />
                          </div>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={addShotItem}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          >
                            Add Shot
                          </button>
                          <button
                            onClick={() => {
                              setShowAddShot(false)
                              setNewShotItem({})
                            }}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      {shotList.map((shot, index) => (
                        <div
                          key={index}
                          className={`bg-gray-800 rounded-lg p-4 border-l-4 ${
                            shot.completed ? 'border-green-500 bg-green-500/5' : 'border-gray-600'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <button
                                  onClick={() => toggleShotCompleted(index)}
                                  className={`p-1 rounded ${
                                    shot.completed 
                                      ? 'text-green-500 bg-green-500/10' 
                                      : 'text-gray-400 hover:text-white'
                                  }`}
                                >
                                  <CheckIcon className="w-4 h-4" />
                                </button>
                                <h4 className={`font-medium ${shot.completed ? 'text-green-400 line-through' : 'text-white'}`}>
                                  {shot.title}
                                </h4>
                                {shot.priority && (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(shot.priority)}`}>
                                    {shot.priority}
                                  </span>
                                )}
                              </div>
                              <p className={`text-sm ${shot.completed ? 'text-gray-500' : 'text-gray-300'}`}>
                                {shot.description}
                              </p>
                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                                {shot.estimated_time && (
                                  <span>~{shot.estimated_time} min</span>
                                )}
                                {shot.notes && (
                                  <span>{shot.notes}</span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => removeShotItem(index)}
                              className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {shotList.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <CameraIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No shots added yet</p>
                          <p className="text-sm">Click "Add Shot" to create your shot list</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Kit List Tab */}
                {activeTab === 'kit-list' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-white">Kit List</h3>
                        <p className="text-sm text-gray-400">
                          {kitCompletionPercentage}% packed ({kitList.filter(item => item.checked).length}/{kitList.length} items)
                        </p>
                      </div>
                      <button
                        onClick={() => setShowAddKit(true)}
                        className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        <span>Add Item</span>
                      </button>
                    </div>

                    {showAddKit && (
                      <div className="bg-gray-800 rounded-lg p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Item *</label>
                            <input
                              type="text"
                              value={newKitItem.item || ''}
                              onChange={(e) => {
                                console.log('Item input changed:', e.target.value)
                                setNewKitItem(prev => {
                                  const updated = { ...prev, item: e.target.value }
                                  console.log('Updated newKitItem state:', updated)
                                  return updated
                                })
                              }}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Canon EOS R5"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
                            <select
                              value={newKitItem.category || 'camera'}
                              onChange={(e) => {
                                console.log('Category changed:', e.target.value)
                                setNewKitItem(prev => {
                                  const updated = { ...prev, category: e.target.value as KitListItem['category'] }
                                  console.log('Updated newKitItem state:', updated)
                                  return updated
                                })
                              }}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="camera">Camera</option>
                              <option value="lens">Lens</option>
                              <option value="lighting">Lighting</option>
                              <option value="audio">Audio</option>
                              <option value="accessories">Accessories</option>
                              <option value="backup">Backup</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Quantity</label>
                            <input
                              type="number"
                              value={newKitItem.quantity || 1}
                              onChange={(e) => {
                                console.log('Quantity changed:', e.target.value)
                                setNewKitItem(prev => {
                                  const updated = { ...prev, quantity: parseInt(e.target.value) || 1 }
                                  console.log('Updated newKitItem state:', updated)
                                  return updated
                                })
                              }}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              min="1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                            <input
                              type="text"
                              value={newKitItem.notes || ''}
                              onChange={(e) => {
                                console.log('Notes changed:', e.target.value)
                                setNewKitItem(prev => {
                                  const updated = { ...prev, notes: e.target.value }
                                  console.log('Updated newKitItem state:', updated)
                                  return updated
                                })
                              }}
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Additional notes"
                            />
                          </div>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => {
                              console.log('Add Item button clicked')
                              addKitItem()
                            }}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          >
                            Add Item
                          </button>
                          <button
                            onClick={() => {
                              setShowAddKit(false)
                              setNewKitItem({})
                            }}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      {kitList.map((item, index) => (
                        <div
                          key={index}
                          className={`bg-gray-800 rounded-lg p-4 border-l-4 ${
                            item.checked ? 'border-green-500 bg-green-500/5' : 'border-gray-600'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => toggleKitItemChecked(index)}
                                className={`p-1 rounded ${
                                  item.checked 
                                    ? 'text-green-500 bg-green-500/10' 
                                    : 'text-gray-400 hover:text-white'
                                }`}
                              >
                                <CheckIcon className="w-4 h-4" />
                              </button>
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-400">
                                  {getCategoryIcon(item.category)}
                                </span>
                                <span className={`font-medium ${item.checked ? 'text-green-400 line-through' : 'text-white'}`}>
                                  {item.item}
                                </span>
                                {item.quantity && item.quantity > 1 && (
                                  <span className="text-sm text-gray-400">(x{item.quantity})</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {item.notes && (
                                <span className="text-sm text-gray-400">{item.notes}</span>
                              )}
                              <button
                                onClick={() => removeKitItem(index)}
                                className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {kitList.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <WrenchScrewdriverIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No kit items added yet</p>
                          <p className="text-sm">Click "Add Item" to create your kit list</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Prep Notes Tab */}
                {activeTab === 'prep-notes' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Weather Notes
                          </label>
                          <textarea
                            value={formData.weather_notes || ''}
                            onChange={(e) => handleInputChange('weather_notes', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            placeholder="Weather forecast, lighting conditions, etc."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Parking Information
                          </label>
                          <textarea
                            value={formData.parking_info || ''}
                            onChange={(e) => handleInputChange('parking_info', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            placeholder="Parking location, permits needed, etc."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Access Instructions
                          </label>
                          <textarea
                            value={formData.access_instructions || ''}
                            onChange={(e) => handleInputChange('access_instructions', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            placeholder="How to access the location, entry codes, etc."
                          />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Emergency Contact
                          </label>
                          <input
                            type="text"
                            value={formData.emergency_contact || ''}
                            onChange={(e) => handleInputChange('emergency_contact', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Emergency contact name and phone"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Special Requirements
                          </label>
                          <textarea
                            value={formData.special_requirements || ''}
                            onChange={(e) => handleInputChange('special_requirements', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            placeholder="Special equipment, permits, or requirements"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Client Prep Notes
                          </label>
                          <textarea
                            value={formData.client_prep_notes || ''}
                            onChange={(e) => handleInputChange('client_prep_notes', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            placeholder="What the client should bring or prepare"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Backup Plan
                      </label>
                      <textarea
                        value={formData.backup_plan || ''}
                        onChange={(e) => handleInputChange('backup_plan', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        placeholder="Backup plan in case of weather, equipment failure, etc."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Post-Shoot Notes
                      </label>
                      <textarea
                        value={formData.post_shoot_notes || ''}
                        onChange={(e) => handleInputChange('post_shoot_notes', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        placeholder="Notes to remember after the shoot"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-700">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ShootDetailsModal
